import { useState, useRef, useEffect } from "react";
import apiService from "src/services/api.service";

const STATES = {
  IDLE: "idle",
  LOADING_SUGGESTION: "loading_suggestion",
  SUGGESTION_READY: "suggestion_ready",
  SAVING: "saving",
  ERROR: "error",
};

const AISuggestCreativeModal = ({ offerId, onClose, onSuccess, openToast }) => {
  const [status, setStatus] = useState(STATES.IDLE);
  const [type, setType] = useState("Email");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [existingCount, setExistingCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [note]);

  const handleSuggest = () => {
    setStatus(STATES.LOADING_SUGGESTION);
    setErrorMessage("");
    apiService
      .suggestCreative(offerId, type)
      .then((response) => {
        const suggestion = response.data?.suggestion;
        setName(suggestion?.name || "");
        setNote(suggestion?.note || "");
        if (suggestion?.type) setType(suggestion.type);
        setExistingCount(response.data?.existing_creatives_count || 0);
        setStatus(STATES.SUGGESTION_READY);
      })
      .catch((error) => {
        const statusCode = error?.response?.status;
        let msg = "An error occurred. Please try again.";
        if (statusCode === 404) {
          msg = "Offer not found.";
        } else if (statusCode === 400) {
          msg =
            "No active creatives found for this offer. Please add a creative manually first.";
        } else if (statusCode === 500) {
          msg = "AI service is currently unavailable. Please try again later.";
        } else if (!error?.response) {
          msg = "Connection error. Please try again.";
        }
        setErrorMessage(msg);
        setStatus(STATES.ERROR);
      });
  };

  const handleApprove = () => {
    setStatus(STATES.SAVING);
    setErrorMessage("");
    apiService
      .approveCreative(offerId, { name, note, type })
      .then(() => {
        openToast("success", "Creative created successfully.");
        onSuccess();
      })
      .catch(() => {
        setErrorMessage("Failed to save creative. Please try again.");
        openToast("error", "Failed to save creative.");
        setStatus(STATES.SUGGESTION_READY);
      });
  };

  const isNameEmpty = !name || name.trim() === "";
  const isBusy =
    status === STATES.LOADING_SUGGESTION || status === STATES.SAVING;

  return (
    <div className="NewStrategyModalOverlay z-[99]">
      <div className="NewStrategyModal" style={{ maxWidth: "540px" }}>
        {/* Header */}
        <div className="w-full flex justify-between">
          <div className="NewCreative">
            <img
              alt="AI"
              src="/media/icons/stars-01-purple.svg"
              className="w-[18px] h-[18px]"
            />
            AI Creative Suggestion
          </div>
          <button
            className="CloseIconButton"
            onClick={onClose}
            disabled={isBusy}
          >
            <img
              alt="Close"
              src="/media/icons/closeIcon.svg"
              className="w-5 h-5"
            />
          </button>
        </div>

        <div className="w-full flex flex-col gap-4 items-start self-stretch">
          <div className="h-[1px] self-stretch bg-[#E5E7F0]"></div>

          {/* Type Selector */}
          <div className="NewListTypeButtons">
            <button
              onClick={() => !isBusy && setType("Email")}
              className={
                type === "Email" ? "NewListChosenButton" : "NewListButton"
              }
              disabled={isBusy}
            >
              Email
              {type === "Email" && (
                <span className="ChosenMarkerListType"></span>
              )}
            </button>
            <img
              alt="Separator"
              src="/media/icons/separate.png"
              className="h-[22px] w-[1px]"
            />
            <button
              onClick={() => !isBusy && setType("SMS")}
              className={
                type === "SMS" ? "NewListChosenButton" : "NewListButton"
              }
              disabled={isBusy}
            >
              SMS
              {type === "SMS" && (
                <span className="ChosenMarkerListType"></span>
              )}
            </button>
          </div>

          {/* IDLE STATE: Generate button */}
          {status === STATES.IDLE && (
            <div className="w-full flex flex-col items-center gap-4 py-6">
              <img
                alt="AI"
                src="/media/icons/stars-01-purple.svg"
                className="w-10 h-10 opacity-60"
              />
              <div
                className="text-[#8185A8] text-sm text-center"
                style={{ fontFamily: "Inter" }}
              >
                Select a type and generate an AI-powered creative suggestion
                based on your existing creatives.
              </div>
              <button className="PrimaryButton" onClick={handleSuggest}>
                <img
                  alt="Generate"
                  src="/media/icons/stars-01.svg"
                  className="w-4 h-4 brightness-0 invert"
                />
                Generate Suggestion
              </button>
            </div>
          )}

          {/* LOADING STATE */}
          {status === STATES.LOADING_SUGGESTION && (
            <div className="w-full flex flex-col items-center gap-4 py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7F56D9]"></div>
              <div
                className="text-[#15151F] text-sm font-medium"
                style={{ fontFamily: "Inter" }}
              >
                AI is generating a suggestion...
              </div>
              <div
                className="text-[#8185A8] text-xs"
                style={{ fontFamily: "Inter" }}
              >
                This may take a few seconds.
              </div>
            </div>
          )}

          {/* ERROR STATE */}
          {status === STATES.ERROR && (
            <div className="w-full flex flex-col items-center gap-4 py-6">
              <div
                className="w-full flex p-3 items-center gap-2 rounded-lg bg-[#FFF0F0] border border-[#FFD2D2]"
              >
                <img
                  alt="Error"
                  src="/media/icons/pending.svg"
                  className="w-4 h-4"
                />
                <div
                  className="text-[#810D0D] text-sm flex-1"
                  style={{ fontFamily: "Inter" }}
                >
                  {errorMessage}
                </div>
              </div>
              <button className="PrimaryButton" onClick={handleSuggest}>
                Try Again
              </button>
            </div>
          )}

          {/* SUGGESTION READY / SAVING STATE */}
          {(status === STATES.SUGGESTION_READY || status === STATES.SAVING) && (
            <>
              {existingCount > 0 && (
                <div
                  className="w-full flex px-3 py-2 items-center gap-2 rounded-lg bg-[#F4F0FF] border border-[#E0D5F5]"
                >
                  <img
                    alt="Info"
                    src="/media/icons/stars-01-purple.svg"
                    className="w-4 h-4"
                  />
                  <div
                    className="text-[#5B3E9E] text-xs"
                    style={{ fontFamily: "Inter" }}
                  >
                    Generated based on {existingCount} active creative
                    {existingCount !== 1 ? "s" : ""}.
                  </div>
                </div>
              )}

              <div className="NewStrategyInputSection">
                <div className="NewStrategyInputTitle">
                  {type === "Email" ? "Subject Line" : "SMS Title"}
                </div>
                <input
                  className="NewStrategyInput"
                  type="text"
                  placeholder="Enter creative name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === STATES.SAVING}
                />
              </div>

              <div
                className={
                  type === "SMS"
                    ? "NewStrategyInputSection hasTextarea"
                    : "NewStrategyInputSection"
                }
              >
                <div className="NewStrategyInputTitle">
                  {type === "Email" ? "Creative Note" : "Message"}
                </div>
                {type === "SMS" ? (
                  <textarea
                    ref={textareaRef}
                    className="NewStrategyTextarea"
                    placeholder="Enter message content"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={status === STATES.SAVING}
                    rows={4}
                  />
                ) : (
                  <input
                    className="NewStrategyInput"
                    type="text"
                    placeholder="Enter note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={status === STATES.SAVING}
                  />
                )}
              </div>

              <div className="flex items-center gap-1.5 self-stretch">
                <div
                  className="flex h-[22px] px-[8px] items-center rounded-[34px] bg-[#F4F0FF]"
                >
                  <span
                    className="text-[#7F56D9] text-[11px] font-semibold"
                    style={{ fontFamily: "Inter" }}
                  >
                    {type}
                  </span>
                </div>
              </div>

              <div className="h-[1px] self-stretch bg-[#E5E7F0]"></div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="CloseButton"
                  onClick={onClose}
                  disabled={status === STATES.SAVING}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="CloseButton"
                  onClick={handleSuggest}
                  disabled={status === STATES.SAVING}
                >
                  <img
                    alt="Regenerate"
                    src="/media/icons/refresh-cw-01.svg"
                    className="w-3.5 h-3.5"
                  />
                  Regenerate
                </button>
                <button
                  type="button"
                  className="CreateButton"
                  onClick={handleApprove}
                  disabled={isNameEmpty || status === STATES.SAVING}
                >
                  {status === STATES.SAVING ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    "Approve & Save"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AISuggestCreativeModal;
